import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Inbox, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Modal } from '@/components/ui/Modal';
import {
  useImportMedcomReferral,
  useMedcomReferralExchanges,
  type MedcomReferralExchange,
} from '@/hooks/useReferrals';
import { UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { AttachmentStatusBadge } from '@/components/referrals/AttachmentStatusBadge';
import type { MedcomWaitingAttachment } from '@/hooks/useReferrals';

export function MedcomReferralImportModal({
  clinicId,
  onClose,
}: {
  clinicId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMedcomReferralExchanges(clinicId);
  const importMedcom = useImportMedcomReferral();
  const inFlightRef = useRef(new Set<string>());
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());
  const exchanges = data?.exchanges ?? [];

  function importExchange(exchangeId: string) {
    if (inFlightRef.current.has(exchangeId)) return;
    inFlightRef.current.add(exchangeId);
    setPendingIds((current) => {
      const next = new Set(current);
      next.add(exchangeId);
      return next;
    });
    importMedcom.mutate(
      { action: 'import', clinic_id: clinicId, exchange_id: exchangeId },
      {
        onSettled: () => {
          inFlightRef.current.delete(exchangeId);
          setPendingIds((current) => {
            const next = new Set(current);
            next.delete(exchangeId);
            return next;
          });
        },
      },
    );
  }

  return (
    <Modal title={t('referrals.medcom_fetch')} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="mb-4">
            <ErrorAlert message={t('referrals.medcom_load_error')} />
          </div>
        )}
        {importMedcom.isError && (
          <div className="mb-4">
            <ErrorAlert message={t('referrals.medcom_import_error')} />
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
          </div>
        ) : (
          <>
            {data?.truncated ? (
              <p className={UI_MUTED}>{t('referrals.medcom_truncated')}</p>
            ) : null}
            <WaitingAttachments items={data?.waiting_attachments ?? []} />
            {exchanges.length === 0 ? (
              <div className="mt-12 text-center">
                <Inbox className="mx-auto h-10 w-10 text-ds-muted" />
                <h3 className={`mt-4 ${UI_SECTION}`}>{t('referrals.medcom_empty_title')}</h3>
                <p className={`mt-1 ${UI_MUTED}`}>{t('referrals.medcom_empty_subtitle')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {exchanges.map((exchange) => (
                  <ExchangeRow
                    key={exchange.exchange_id}
                    exchange={exchange}
                    pending={pendingIds.has(exchange.exchange_id)}
                    onImport={importExchange}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function ExchangeRow({
  exchange,
  pending,
  onImport,
}: {
  exchange: MedcomReferralExchange;
  pending: boolean;
  onImport: (exchangeId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-ds-line px-3 py-3">
      <div className="min-w-0">
        <div className="font-medium text-ds-text">
          {exchange.letter_identifier ?? '—'}
          {exchange.kind && (
            <span className="ml-2 rounded bg-ds-neutral-tint px-1.5 py-0.5 text-xs font-medium text-ds-muted">
              {exchange.kind}
            </span>
          )}
        </div>
        <div className={UI_SECONDARY}>{exchange.sender_ean ?? '—'}</div>
        <div className={UI_MUTED}>
          {exchange.imported_at
            ? new Date(exchange.imported_at).toLocaleDateString('da-DK')
            : '—'}
        </div>
        {(exchange.attachment_count ?? 0) > 0 && (
          <div className="mt-1">
            <AttachmentStatusBadge
              total={exchange.attachment_count ?? 0}
              received={exchange.attachments_received ?? 0}
            />
          </div>
        )}
        {(exchange.received_count ?? 1) > 1 && (
          <div
            className="mt-1 inline-flex items-center rounded-full bg-ds-amber-tint px-2 py-0.5 text-xs font-medium text-ds-amber-ink"
            data-testid="medcom-already-received"
          >
            {t('referrals.medcom_already_received', { count: exchange.received_count })}
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        {exchange.already_imported ? (
          <>
            <div className={UI_SECONDARY}>{t('referrals.medcom_imported')}</div>
            {exchange.referral_id ? (
              <Link
                to={`/henvisninger/${exchange.referral_id}`}
                className="text-ds-teal hover:underline"
                data-testid="medcom-view-referral"
                data-letter-identifier={exchange.letter_identifier ?? undefined}
              >
                {exchange.letter_identifier
                  ? t('referrals.medcom_view_referral_letter', {
                      letter: exchange.letter_identifier,
                      defaultValue: 'Se henvisning {{letter}} →',
                    })
                  : t('referrals.medcom_view_referral')}
              </Link>
            ) : null}
          </>
        ) : (
          <Button type="button" onClick={() => onImport(exchange.exchange_id)} disabled={pending}>
            {t('referrals.medcom_import')}
          </Button>
        )}
      </div>
    </div>
  );
}

// C13 (BIN01-M 3.3, 4.10, 4.11): a MEDBIN that arrived before its message is
// shown as waiting; it is coupled automatically once the message arrives.
function WaitingAttachments({ items }: { items: MedcomWaitingAttachment[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <div
      className="rounded-lg border border-ds-amber-line bg-ds-amber-tint px-3 py-3"
      data-testid="medcom-waiting-attachments"
    >
      <div className="text-sm font-medium text-ds-amber-ink">
        {t('referrals.medcom_waiting_title', { count: items.length })}
      </div>
      <p className="mt-0.5 text-xs text-ds-amber-ink">
        {t('referrals.medcom_waiting_hint')}
      </p>
      <ul className="mt-2 space-y-1.5">
        {items.map((item) => (
          <li key={item.exchange_id} className="text-sm text-ds-text">
            <span className="font-mono text-xs">{item.object_identifier}</span>
            <span className={`ml-2 ${UI_MUTED}`}>
              {[
                item.kind,
                [item.object_code, item.object_extension_code].filter(Boolean).join(' · ') || null,
                item.original_object_size !== null ? `${item.original_object_size} B` : null,
                item.received_at ? new Date(item.received_at).toLocaleString('da-DK') : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className="ml-2 inline-flex rounded-full bg-ds-amber-tint px-2 py-0.5 text-xs font-medium text-ds-amber-ink">
              {t('referrals.medcom_waiting_badge')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
